/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The address of the aanbiedersoverzicht and of each provider's profile page —
 * one segment, the same in both languages: /<lang>/providers and
 * /<lang>/providers/<slug>.
 *
 * Until 2026-10-15 the segment was `nea-providers`, from the days the overview
 * was the NEa list; the list lapsed on 1 October 2026 and the overview is our
 * own register, so the address no longer names it. Every old address answers
 * with a 301 to its new one (server/spaRenderer.ts; App.tsx does the same
 * client-side for the dev server, which has no redirects of its own).
 *
 * Plain ESM JavaScript with zero imports, like the sibling segment constants
 * (BEGRIPPEN_SEGMENT in begrippen.js, CHARGER_BRANDS_SEGMENT in
 * chargerCatalog.js): the client, the server and the bake (plain Node) share
 * it. The two zero-import route lists (staticRoutes.js, routePageModules.js)
 * spell the segment out; src/__tests__/routePageModules.test.ts holds the bake
 * to this constant and src/lib/__tests__/providerRoutes.test.ts keeps the old
 * segment out of every file but the few that must still know it.
 *
 * Not addresses, and so not renamed with it: the API path /api/nea-providers,
 * the data file nea_providers.json, the stored page keys named `nea` (page
 * structure, SEO row, image slot, admin nav) and the analytics surface name
 * `nea_providers`.
 */

/** The route segment of the overview and of every profile page. */
export const PROVIDERS_SEGMENT = 'providers';

/**
 * Former segments of the same pages, newest first: `nea-providers` (until
 * 2026-10-15) and `aanbieders` (the profile pages until 2026-05-15). Read by
 * the server's 301s, by App.tsx's client-side redirect and by the partner
 * statistics, whose stored page views keep the old paths for the life of the
 * store.
 *
 * @type {readonly string[]}
 */
export const LEGACY_PROVIDERS_SEGMENTS = Object.freeze(['nea-providers', 'aanbieders']);

/**
 * @param {string} lang
 * @returns {'nl' | 'en'}
 */
const langOf = (lang) => (lang === 'en' ? 'en' : 'nl');

/**
 * The overview's path: `/nl/providers`.
 *
 * @param {string} lang  `nl` or `en`; anything else reads as `nl`
 * @returns {string}
 */
export function providerHubPath(lang) {
  return `/${langOf(lang)}/${PROVIDERS_SEGMENT}`;
}

/**
 * A provider's profile path: `/nl/providers/greenflux-12`. The slug is
 * providerSlug's (src/lib/providerPayloads.js), passed as it is.
 *
 * @param {string} lang  `nl` or `en`; anything else reads as `nl`
 * @param {string} slug
 * @returns {string}
 */
export function providerProfilePath(lang, slug) {
  return `${providerHubPath(lang)}/${slug}`;
}
